/**
 * The forgotten-workout question (`D245`), asked over whatever screen is
 * showing. Mounted once by the shell beside `<WorkoutRuntime />`.
 *
 * ── WHEN IT LOOKS ──────────────────────────────────────────────────────────
 * Once the stored session is read, every time the app comes back to the
 * front, and every five minutes while it stays there (a phone left awake on
 * a bench). What it asks about is `forgotten-model.ts`; the person's typical
 * workout is read from the session lists already in the Query cache —
 * nothing is fetched for it, so offline it asks with what the device holds.
 *
 * ── WHAT EACH ANSWER WRITES ────────────────────────────────────────────────
 * - "Encerrar treino": `actions.endAt(chosen)` — the ordinary end, through
 *   the same outbox write and `POST …/end` as "Encerrar", so the summary and
 *   the check-in are promised as for any ending (`D116`), and the paused
 *   time travels with it. Then the workout screen, which shows the summary.
 * - "Continuar treinando" (and closing the sheet): `actions.keepOpen()`,
 *   which holds the question off for another threshold. Local only.
 * - "Descartar treino": the `abandoned` end through the outbox, as
 *   "Cancelar treino" queues it (`D220`), stamped at the last set rather
 *   than now — the hours since were not training, and an abandoned
 *   session's minutes still count (`D177`). Then the device's copy goes.
 *   No follow-up is promised: the route skips both for `abandoned`.
 *
 * No push is sent for any of this (`D100`, `D152`): the question is in-app.
 */

import { workoutKeys } from "@repo/client/data/workout";
import type { SessionListOutput } from "@repo/contract/training";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { useAppSearch, useOpenLayer } from "../../../app/layers";
import { screenOf } from "../../../app/search";
import { abandonEnd } from "../../../data/account-writes";
import { OutboxContext } from "../../../data/outbox-runner";
import { onAppActiveChange } from "../../../native";
import { useWorkoutActions, useWorkoutHydrated, workoutStore } from "../store";
import { pausedSecondsAt } from "../store/session-model";
import {
  type EndChoiceKind,
  type ForgottenPrompt,
  forgottenPrompt,
  forgottenThresholdMs,
  lastSetAt,
  typicalDurationsMs,
} from "./forgotten-model";
import { ForgottenSheet } from "./forgotten-sheet";

const RECHECK_MS = 5 * 60 * 1000;

interface Asking {
  readonly prompt: ForgottenPrompt;
  readonly sessionId: string;
}

/** Every session list the cache holds, any range, flattened. */
function cachedSessions(
  lists: readonly (readonly [unknown, SessionListOutput | undefined])[]
): SessionListOutput["sessions"] {
  const byId = new Map<string, SessionListOutput["sessions"][number]>();
  for (const [, list] of lists) {
    for (const session of list?.sessions ?? []) {
      byId.set(session.sessionId, session);
    }
  }
  return [...byId.values()];
}

export function ForgottenWorkoutAsk() {
  const hydrated = useWorkoutHydrated();
  const client = useQueryClient();
  const actions = useWorkoutActions();
  const runner = useContext(OutboxContext);
  const openLayer = useOpenLayer();
  const onWorkout = screenOf(useAppSearch()) === "workout";
  const [asking, setAsking] = useState<Asking | null>(null);
  const [chosen, setChosen] = useState<EndChoiceKind>("last");
  const [pending, setPending] = useState(false);

  const look = useCallback(() => {
    const { session } = workoutStore.getState();
    if (session === null || session.ended !== null) {
      setAsking(null);
      return;
    }
    const lists = client.getQueriesData<SessionListOutput>({
      queryKey: workoutKeys.sessionsAll,
    });
    const threshold = forgottenThresholdMs(
      typicalDurationsMs(cachedSessions(lists))
    );
    const prompt = forgottenPrompt(session, Date.now(), threshold);
    if (prompt === null) {
      return;
    }
    setChosen("last");
    setAsking({ prompt, sessionId: session.sessionId });
  }, [client]);

  useEffect(() => {
    if (!hydrated) {
      return;
    }
    look();
    const offActive = onAppActiveChange((active) => {
      if (active) {
        look();
      }
    });
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        look();
      }
    }, RECHECK_MS);
    return () => {
      offActive();
      window.clearInterval(timer);
    };
  }, [hydrated, look]);

  const done = useCallback(() => {
    setPending(false);
    setAsking(null);
  }, []);

  const keep = useCallback(() => {
    if (pending) {
      return;
    }
    actions.keepOpen();
    done();
  }, [actions, done, pending]);

  const end = useCallback(() => {
    const choice = asking?.prompt.choices.find((c) => c.kind === chosen);
    if (asking === null || choice === undefined || pending) {
      return;
    }
    setPending(true);
    actions.endAt(choice.at).then(done, (error: unknown) => {
      console.error(
        "[workout] the forgotten end did not reach the outbox",
        error
      );
      done();
    });
    if (!onWorkout) {
      openLayer({ screen: "workout" });
    }
  }, [actions, asking, chosen, done, onWorkout, openLayer, pending]);

  const discard = useCallback(() => {
    const { session } = workoutStore.getState();
    if (
      asking === null ||
      session === null ||
      session.sessionId !== asking.sessionId ||
      runner === null ||
      pending
    ) {
      return;
    }
    setPending(true);
    const at = lastSetAt(session) ?? session.startedAt;
    const paused = pausedSecondsAt(session, at);
    const entry = abandonEnd(session.sessionId, {
      endReason: "abandoned",
      endedAt: new Date(at).toISOString(),
      ...(paused > 0 ? { pausedSeconds: paused } : {}),
    });
    runner.enqueue(entry).then(
      () => {
        workoutStore.setState({ session: null });
        done();
      },
      (error: unknown) => {
        console.error("[workout] the discard did not reach the outbox", error);
        setPending(false);
      }
    );
  }, [asking, done, pending, runner]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        keep();
      }
    },
    [keep]
  );

  if (asking === null) {
    return null;
  }
  return (
    <ForgottenSheet
      chosen={chosen}
      onChoose={setChosen}
      onDiscard={discard}
      onEnd={end}
      onKeep={keep}
      onOpenChange={onOpenChange}
      pending={pending}
      prompt={asking.prompt}
    />
  );
}
